import React, { useEffect, useRef, useState } from 'react'
import CaretDownIcon from '@tecuity/sos-icons/react/caret-down'
import './MultiSelectDropdown.scss'

/**
 * MultiSelectDropdown
 *
 * A searchable, multi-select dropdown built with native ARIA patterns.
 *
 * Props:
 *   options           - Array of { value, label }
 *   value             - Array of selected values (controlled)
 *   onChange          - Called with the updated array of selected values
 *   label             - Optional visible label rendered above the trigger
 *   ariaLabel         - Accessible name used when no visible label is rendered (required if label is omitted)
 *   placeholder       - Placeholder text shown when nothing is selected
 *   filterPlaceholder - Placeholder text for the search input inside the dropdown
 *   disabled          - Disables the trigger
 */
const MultiSelectDropdown = ({
  options = [],
  value = [],
  onChange,
  label,
  ariaLabel,
  placeholder = 'Select options...',
  filterPlaceholder = 'Search...',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [filterValue, setFilterValue] = useState('')
  const [activeIndex, setActiveIndex] = useState(-1)
  const [announcement, setAnnouncement] = useState('')
  const containerRef = useRef(null)
  const triggerRef = useRef(null)
  const filterInputRef = useRef(null)
  const listboxRef = useRef(null)
  const uid = useRef(`msd-${Math.random().toString(36).slice(2, 8)}`)
  const labelId = `${uid.current}-label`
  const listboxId = `${uid.current}-listbox`

  const selectedOptions = options.filter((opt) => value.includes(opt.value))

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(filterValue.toLowerCase())
  )

  const activeOptionId =
    activeIndex >= 0 && filteredOptions[activeIndex]
      ? `${uid.current}-option-${filteredOptions[activeIndex].value}`
      : undefined

  // Focus the filter input whenever the dropdown opens; reset state on close.
  useEffect(() => {
    if (isOpen) {
      filterInputRef.current?.focus()
    } else {
      setFilterValue('')
      setActiveIndex(-1)
    }
  }, [isOpen])

  const handleToggle = (optionValue) => {
    const opt = options.find((o) => o.value === optionValue)
    const optLabel = opt?.label ?? ''
    let newValue
    if (value.includes(optionValue)) {
      newValue = value.filter((v) => v !== optionValue)
      setAnnouncement(
        `${optLabel} removed. ${newValue.length} option${newValue.length !== 1 ? 's' : ''} selected.`
      )
    } else {
      newValue = [...value, optionValue]
      setAnnouncement(
        `${optLabel} selected. ${newValue.length} option${newValue.length !== 1 ? 's' : ''} selected.`
      )
    }
    onChange(newValue)
  }

  const handleRemoveTag = (e, optionValue) => {
    e.stopPropagation()
    const opt = options.find((o) => o.value === optionValue)
    const optLabel = opt?.label ?? ''
    const newValue = value.filter((v) => v !== optionValue)
    setAnnouncement(
      `${optLabel} removed. ${newValue.length} option${newValue.length !== 1 ? 's' : ''} selected.`
    )
    onChange(newValue)
  }

  const closePopover = () => {
    setIsOpen(false)
    triggerRef.current?.focus()
  }

  const handleClickOutside = (event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setIsOpen(false)
    }
  }

  const handleFilterKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (filteredOptions.length > 0) {
        setActiveIndex(0)
        listboxRef.current?.focus()
      }
    } else if (e.key === 'Escape') {
      e.stopPropagation()
      closePopover()
    }
  }

  const handleListboxKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (activeIndex <= 0) {
        setActiveIndex(-1)
        filterInputRef.current?.focus()
      } else {
        setActiveIndex((prev) => prev - 1)
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (activeIndex >= 0 && filteredOptions[activeIndex]) {
        handleToggle(filteredOptions[activeIndex].value)
      }
    } else if (e.key === 'Escape') {
      e.stopPropagation()
      closePopover()
    }
  }

  return (
    <div
      ref={containerRef}
      className="multi-select-dropdown"
      // Capture outside clicks on the document via bubbling
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget)) {
          setIsOpen(false)
        }
      }}
    >
      {/* Visually hidden live region — announces selection changes to screen readers */}
      <div
        className="multi-select-dropdown__live-region"
        aria-live="polite"
        aria-atomic="true"
      >
        {announcement}
      </div>

      {label && (
        <span id={labelId} className="multi-select-dropdown__label">
          {label}
        </span>
      )}

      <div
        ref={triggerRef}
        className={[
          'multi-select-dropdown__trigger',
          isOpen ? 'multi-select-dropdown__trigger--open' : '',
          disabled ? 'multi-select-dropdown__trigger--disabled' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => {
          if (!disabled) {
            setIsOpen((prev) => !prev)
          }
        }}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-labelledby={label ? labelId : undefined}
        aria-label={!label ? ariaLabel : undefined}
        aria-disabled={disabled}
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault()
            setIsOpen((prev) => !prev)
          }
          if (e.key === 'Escape') {
            setIsOpen(false)
          }
        }}
      >
        <div className="multi-select-dropdown__tags-area">
          {selectedOptions.length === 0 ? (
            <span className="multi-select-dropdown__placeholder">{placeholder}</span>
          ) : (
            selectedOptions.map((opt) => (
              <span key={opt.value} className="multi-select-dropdown__tag">
                <span className="multi-select-dropdown__tag-label">{opt.label}</span>
                {!disabled && (
                  <button
                    className="multi-select-dropdown__tag-remove"
                    onClick={(e) => handleRemoveTag(e, opt.value)}
                    onKeyDown={(e) => {
                      // Prevent Enter/Space from also toggling the dropdown.
                      if (e.key === 'Enter' || e.key === ' ') e.stopPropagation()
                    }}
                    aria-label={`Remove ${opt.label}`}
                  >
                    ✕
                  </button>
                )}
              </span>
            ))
          )}
        </div>

        {/* aria-hidden: purely decorative */}
        <span
          aria-hidden="true"
          className={`multi-select-dropdown__arrow ${isOpen ? 'multi-select-dropdown__arrow--open' : ''}`}
        >
          <CaretDownIcon />
        </span>
      </div>

      {isOpen && (
        <div
          className="multi-select-dropdown__popover-container"
          // Swallow outside-click events before they propagate
          onClick={(e) => {
            if (!containerRef.current?.contains(e.target)) handleClickOutside(e)
          }}
        >
          {/*
           * The filter input lives OUTSIDE the listbox so that the listbox
           * only ever contains role="option" elements (WCAG / ARIA requirement).
           */}
          <input
            ref={filterInputRef}
            type="text"
            className="multi-select-dropdown__filter"
            value={filterValue}
            onChange={(e) => {
              setFilterValue(e.target.value)
              setActiveIndex(-1)
            }}
            placeholder={filterPlaceholder}
            aria-label={filterPlaceholder}
            aria-controls={listboxId}
            aria-autocomplete="list"
            onKeyDown={handleFilterKeyDown}
          />

          {/*
           * role="listbox" with aria-multiselectable.
           * tabIndex={-1} allows programmatic focus (arrow-key nav) without
           * adding it to the natural tab order.
           * aria-activedescendant keeps DOM focus on the listbox while the
           * screen reader announces the currently highlighted option.
           */}
          <ul
            ref={listboxRef}
            role="listbox"
            id={listboxId}
            aria-multiselectable="true"
            aria-labelledby={label ? labelId : undefined}
            aria-label={!label ? ariaLabel : undefined}
            aria-activedescendant={activeOptionId}
            tabIndex={-1}
            className="multi-select-dropdown__list"
            onKeyDown={handleListboxKeyDown}
          >
            {filteredOptions.length === 0 ? (
              <li role="option" aria-selected="false" aria-disabled="true" className="multi-select-dropdown__option multi-select-dropdown__option--empty">
                No options found
              </li>
            ) : (
              filteredOptions.map((opt, index) => {
                const isSelected = value.includes(opt.value)
                const isActive = index === activeIndex
                return (
                  <li
                    key={opt.value}
                    role="option"
                    id={`${uid.current}-option-${opt.value}`}
                    aria-selected={isSelected}
                    onClick={() => handleToggle(opt.value)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={[
                      'multi-select-dropdown__option',
                      isSelected ? 'multi-select-dropdown__option--selected' : '',
                      isActive ? 'multi-select-dropdown__option--active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {/*
                     * Visual check mark is aria-hidden — the selection state is
                     * already communicated via aria-selected on the parent option.
                     */}
                    <span aria-hidden="true" className="multi-select-dropdown__option-check">
                      {isSelected ? '✓' : ''}
                    </span>
                    {opt.label}
                  </li>
                )
              })
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

export default MultiSelectDropdown
export { MultiSelectDropdown }
